import { useState } from "react";
import { useCachedGet } from "@/lib/cache";
import { StatusChip, ClientLine, JOB_STATUSES } from "@/components/app/JobCard";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/app/Layout";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Download, X, ArrowUpRight, FileDown, Trash2, Pencil, Phone, Video, Code2, Sparkles, Loader2, Globe, ChevronDown, Search, ArrowUpDown, ArrowUp, ArrowDown, Upload, ExternalLink } from "lucide-react";
import { viewReport, downloadReport } from "@/lib/report";
import { COUNTRIES, COUNTRY_BY_CODE, fmtSalaryBand, salaryFromInput, salaryToInput } from "@/lib/countries";
import SalaryUnitToggle from "@/components/app/SalaryUnitToggle";
import PhoneLanguageSelect, { normPhoneLang } from "@/components/app/PhoneLanguageSelect";
import PhonePlanEditor, { planCtx, planPayload } from "@/components/app/PhonePlanEditor";
import JobClientFields, { jobClientDefaults, jobClientPayload } from "@/components/app/JobClientFields";
import JdUpload, { applyParsedJd } from "@/components/app/JdUpload";
import FormSection from "@/components/app/FormSection";
import useCloseGuard from "@/lib/useCloseGuard";
import { skillSummary, locationSummary, stageSummary, codingSummary, sectionDone, missingField } from "@/components/app/jobFormSummaries";
import JobSkillsFields from "@/components/app/JobSkillsFields";
import InviteCallFields, { nextSlot } from "@/components/app/InviteCallFields";
import { fromTzInput } from "@/lib/tz";
import CodingRoundSection, { codingDefaults, codingPayload } from "@/components/app/coding/CodingRoundSection";
import BulkInviteDialog from "@/components/app/BulkInviteDialog";
import AnnotationCell from "@/components/app/AnnotationCell";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/perm";
import { stageLabel } from "@/lib/stages";
import PhoneInput from "@/components/app/PhoneInput";
import EditCandidateDialog from "@/components/app/EditCandidateDialog";

const CF_BLANK = { q: "", stage: "", rec: "" };
const REC_OPTIONS = [["strong_hire", "Strong Hire"], ["hire", "Hire"], ["maybe", "Maybe"], ["no_hire", "No Hire"], ["pending", "Pending"]];
const filterCands = (list, f) => {
  const q = f.q.trim().toLowerCase();
  const digits = q.replace(/\D/g, "");
  return list.filter(c => {
    if (f.stage && c.stage !== f.stage) return false;
    if (f.rec && (c.recommendation || "pending") !== f.rec) return false;
    if (!q) return true;
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q)
      || (digits.length >= 3 && (c.phone || "").replace(/\D/g, "").includes(digits));
  });
};

const STAGE_ORDER = ["invited", "screening", "video", "coding", "scorecard", "shortlist", "rejected"];
const sortCands = (list, sort) => {
  if (!sort) return list;
  const { k, dir } = sort;
  const m = dir === "asc" ? 1 : -1;
  const val = (c) => k === "name" ? (c.name || "").toLowerCase() : k === "stage" ? STAGE_ORDER.indexOf(c.stage) : c[k];
  return [...list].sort((a, b) => {
    const x = val(a), y = val(b);
    const nx = x == null || x === -1, ny = y == null || y === -1;
    if (nx || ny) return nx === ny ? 0 : nx ? 1 : -1;
    return (typeof x === "string" ? x.localeCompare(y) : x - y) * m;
  });
};

function SortTh({ k, label, right, sort, onSort }) {
  const active = sort?.k === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 ${right ? "text-right" : "text-left"}`} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" data-testid={`sort-${k}`} onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-slate-800 ${active ? "text-indigo-600" : ""}`}>
        {label}<Icon className={`w-3 h-3 ${active ? "" : "opacity-40"}`} />
      </button>
    </th>
  );
}

const stageColors = {
  invited: "tp-chip",
  screening: "tp-chip tp-chip-indigo",
  video: "tp-chip tp-chip-cyan",
  coding: "tp-chip tp-chip-amber",
  scorecard: "tp-chip tp-chip-indigo",
  incomplete: "tp-chip tp-chip-amber",
  shortlist: "tp-chip tp-chip-emerald",
  rejected: "tp-chip tp-chip-rose",
};

function JobDescription({ text }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > 280;
  return (
    <div className="mt-3">
      <div className={`text-slate-500 text-sm leading-relaxed whitespace-pre-line ${!open && long ? "line-clamp-3" : ""}`} data-testid="job-description">{text}</div>
      {long && (
        <button data-testid="toggle-description" onClick={() => setOpen(o => !o)} className="mt-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-500 inline-flex items-center gap-1">
          {open ? "Show less" : "Show full description"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}

function MustHaves({ items }) {
  const [all, setAll] = useState(false);
  if (!items?.length) return null;
  const LIMIT = 10;
  const shown = all ? items : items.slice(0, LIMIT);
  return (
    <div className="mt-5" data-testid="must-haves">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-medium">Must-have skills</div>
      <div className="flex flex-wrap gap-1.5">
        {shown.map((m) => <span key={m} className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 border border-[color:var(--bd-sub)]">{m}</span>)}
        {items.length > LIMIT && (
          <button data-testid="toggle-must-haves" onClick={() => setAll(a => !a)} className="text-xs px-2.5 py-1 rounded-md text-indigo-600 hover:bg-indigo-50 font-medium">
            {all ? "Show less" : `+${items.length - LIMIT} more`}
          </button>
        )}
      </div>
    </div>
  );
}

function GlanceRow({ label, children, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-[color:var(--bd-sub)] last:border-0">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right" data-testid={testid}>{children}</span>
    </div>
  );
}

function JobHeader({ job, stages, candidateCount }) {
  const loc = job.remote ? "Remote" : [job.city, COUNTRY_BY_CODE[job.country_code]?.name].filter(Boolean).join(", ");
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 mb-6">
      <div className="min-w-0">
        <Link to="/app/jobs" className="text-xs text-slate-500 hover:text-slate-300">← Jobs</Link>
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <h1 className="font-display text-3xl font-bold">{job.title}</h1>
          <StatusChip status={job.status} testid="job-status-chip" />
        </div>
        <ClientLine j={job} className="mt-2 text-sm" />
        <JobDescription text={job.description} />
        <MustHaves items={job.must_haves} />
      </div>
      <aside className="tp-card p-5 self-start" data-testid="role-at-a-glance">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">Role at a glance</div>
        {job.client && <GlanceRow label="Client" testid="job-glance-client">{job.client}</GlanceRow>}
        {job.client_job_id && <GlanceRow label="Client Job ID" testid="job-glance-client-job-id"><span className="font-mono">{job.client_job_id}</span></GlanceRow>}
        {job.account && <GlanceRow label="Account" testid="job-glance-account">{job.account}{job.account_location ? ` · ${job.account_location}` : ""}</GlanceRow>}
        {job.service_line && <GlanceRow label="Service Line" testid="job-glance-service-line">{job.service_line}</GlanceRow>}
        {job.job_type && <GlanceRow label="Job Type" testid="job-glance-job-type">{job.job_type === "fte" ? "Full Time" : "Contract"}</GlanceRow>}
        <GlanceRow label="Location" testid="job-location-chip"><span className="inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5 text-slate-400" />{loc || "Not set"}</span></GlanceRow>
        <GlanceRow label="Salary" testid="job-salary-chip">{fmtSalaryBand(job.salary_min, job.salary_max, job.salary_currency, job.salary_unit) || "Not set"}</GlanceRow>
        <GlanceRow label="Notice period" testid="job-notice">≤ {job.max_notice_days} days</GlanceRow>
        <GlanceRow label="Candidates" testid="job-candidate-count">{candidateCount}</GlanceRow>
        <div className="pt-3">
          <div className="text-xs text-slate-500 mb-2">Interview stages</div>
          <div className="flex flex-wrap gap-1.5">
            {stages.telephonic && <span className="tp-chip tp-chip-cyan" data-testid="stage-chip-telephonic"><Phone className="w-3 h-3 inline mr-1"/>Telephonic</span>}
            {stages.video && <span className="tp-chip tp-chip-cyan" data-testid="stage-chip-video"><Video className="w-3 h-3 inline mr-1"/>Video</span>}
            {stages.coding && <span className="tp-chip tp-chip-amber" data-testid="stage-chip-coding"><Code2 className="w-3 h-3 inline mr-1"/>Coding</span>}
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function JobDetailPage() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const [editCand, setEditCand] = useState(null);
  const [job, reloadJob] = useCachedGet(`/jobs/${jobId}`);
  const [candsData, reloadCands] = useCachedGet(`/jobs/${jobId}/candidates`);
  const cands = candsData || [];
  const [cfState, setCf] = useState(CF_BLANK);
  const cf = { ...cfState, active: !!(cfState.q.trim() || cfState.stage || cfState.rec) };
  const [sort, setSort] = useState(null);
  const toggleSort = (k) => setSort(p => {
    const first = k === "name" || k === "stage" ? "asc" : "desc";
    if (!p || p.k !== k) return { k, dir: first };
    return p.dir === first ? { k, dir: first === "asc" ? "desc" : "asc" } : null;
  });
  const shownCands = sortCands(filterCands(cands, cfState), sort);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const navigate = useNavigate();
  const [edit, setEdit] = useState(null);
  const [invalidTick, setInvalidTick] = useState(0);
  const requestEditClose = useCloseGuard(editOpen, edit, () => { setEditOpen(false); setInvalidTick(0); });
  const [genBusy, setGenBusy] = useState(null); // "knockout" | "video" | null
  const BLANK_INV = { candidate_name: "", candidate_email: "", candidate_phone: "", call_mode: "scheduled", call_at_local: "" };
  const [inv, setInv] = useState(BLANK_INV);
  const jobTz = job?.timezone || "UTC";
  const phoneStage = !!job?.stages?.telephonic;

  const duplicateJob = async () => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/duplicate`);
      toast.success(`Created draft "${data.title}"`);
      navigate(`/app/jobs/${data.id}`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  const load = () => Promise.all([
    reloadJob(),
    reloadCands(),
  ]);

  const invite = async (e) => {
    e.preventDefault();
    try {
      const { call_at_local, ...rest } = inv;
      const body = { ...rest, call_mode: phoneStage ? inv.call_mode : "none",
        call_at: inv.call_mode === "scheduled" && call_at_local ? fromTzInput(call_at_local, jobTz).toISOString() : null };
      const { data } = await api.post(`/jobs/${jobId}/invite`, body);
      const url = `${window.location.origin}/invite/${data.invite_token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      if (data.phone_call_status === "scheduled") {
        toast.success(`Invite created · Phone screen scheduled for ${data.call_time_text}`);
      } else if (data.phone_call_status === "dialing") {
        toast.success("Invite created · Dialing candidate for telephonic round");
      } else if (data.phone_call_status && data.phone_call_status.startsWith("call_failed:")) {
        toast.success("Invite created · Link copied");
        toast.error(`Phone dial failed: ${data.phone_call_status.replace("call_failed:", "")}`);
      } else {
        toast.success("Invite created — link copied");
      }
      setOpen(false);
      setInv(BLANK_INV);
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const openEdit = () => {
    const s = job.stages || { telephonic: true, video: true, coding: true };
    setEdit({
      title: job.title || "",
      description: job.description || "",
      client: job.client || "",
      account: job.account || "",
      status: job.status || "open",
      must_haves: (job.must_haves || []).join(", "),
      nice_to_haves: (job.nice_to_haves || []).join(", "),
      country_code: job.country_code || "US",
      city: job.city || "",
      remote: !!job.remote,
      phone_language: normPhoneLang(job.phone_language),
      phone_plan: job.phone_plan || [],
      salary_currency: job.salary_currency || "USD",
      ...(() => {
        const unit = job.salary_unit || (job.salary_currency === "INR" ? "lpa" : "annual");
        return { salary_unit: unit, salary_min: salaryToInput(job.salary_min, unit), salary_max: salaryToInput(job.salary_max, unit) };
      })(),
      max_notice_days: job.max_notice_days || 60,
      knockout_questions: (job.knockout_questions || []).join("\n"),
      video_questions: (job.video_questions || []).join("\n"),
      stages: { telephonic: !!s.telephonic, video: !!s.video, coding: !!s.coding },
      ...codingDefaults(job),
      ...jobClientDefaults(job),
    });
    setEditOpen(true);
  };

  const generateQuestions = async (kind) => {
    const must = edit.must_haves.split(",").map(s => s.trim()).filter(Boolean);
    const nice = edit.nice_to_haves.split(",").map(s => s.trim()).filter(Boolean);
    if (must.length === 0 && nice.length === 0) {
      toast.error("Please fill the required skills (Must-haves or Nice-to-haves) before generating.");
      return;
    }
    const field = kind === "knockout" ? "knockout_questions" : "video_questions";
    const existing = edit[field].split("\n").map(s => s.trim()).filter(Boolean);
    setGenBusy(kind);
    try {
      const { data } = await api.post("/jobs/generate-questions", {
        kind, title: edit.title, description: edit.description,
        must_haves: must, nice_to_haves: nice, existing,
        count: kind === "knockout" ? 4 : 5,
      });
      const seen = new Set(existing.map(q => q.toLowerCase()));
      const fresh = data.questions.filter(q => !seen.has(q.trim().toLowerCase()));
      setEdit((prev) => ({ ...prev, [field]: [...existing, ...fresh].join("\n") }));
      toast.success(`Added ${fresh.length} new AI-generated question${fresh.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "AI generation failed");
    } finally { setGenBusy(null); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!edit.stages.telephonic && !edit.stages.video && !edit.stages.coding) {
      toast.error("Select at least one interview stage"); return;
    }
    if (!edit.job_type) { toast.error("Select a Job Type (Full Time or Contract)"); return; }
    try {
      await api.put(`/jobs/${jobId}`, {
        title: edit.title,
        description: edit.description,
        ...jobClientPayload(edit),
        status: edit.status,
        must_haves: edit.must_haves.split(",").map(s => s.trim()).filter(Boolean),
        nice_to_haves: edit.nice_to_haves.split(",").map(s => s.trim()).filter(Boolean),
        country_code: edit.country_code || null,
        city: edit.city.trim() || null,
        remote: !!edit.remote,
        phone_language: normPhoneLang(edit.phone_language),
        phone_plan: planPayload(edit.phone_plan),
        salary_currency: edit.salary_currency || "USD",
        salary_min: salaryFromInput(edit.salary_min, edit.salary_unit),
        salary_max: salaryFromInput(edit.salary_max, edit.salary_unit),
        salary_unit: edit.salary_currency === "INR" ? (edit.salary_unit || "annual") : "annual",
        max_notice_days: parseInt(edit.max_notice_days || 60),
        knockout_questions: edit.knockout_questions.split("\n").map(s => s.trim()).filter(Boolean),
        video_questions: edit.video_questions.split("\n").map(s => s.trim()).filter(Boolean),
        stages: edit.stages,
        ...codingPayload(edit),
      });
      toast.success("Job updated");
      setEditOpen(false);
      setInvalidTick(0);
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Invite link copied"));
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/exports/candidates.csv?job_id=${jobId}`;
    window.open(url, "_blank");
  };

  if (!job) return <Layout><div className="text-slate-400">Loading…</div></Layout>;
  const stages = job.stages || { telephonic: true, video: true, coding: true };

  return (
    <Layout
      actions={
        <>
          {can(user, "jobs.manage") && (
          <button data-testid="duplicate-job" onClick={duplicateJob} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <Copy className="w-4 h-4" /> Duplicate
          </button>
          )}
          {job.can_edit && (
            <button data-testid="edit-job" onClick={openEdit} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
              <Pencil className="w-4 h-4" /> Edit job
            </button>
          )}
          {job.can_delete && (
            <button data-testid="delete-job" onClick={async () => {
              if (!window.confirm(`Delete "${job.title}" and all its candidates? This can't be undone.`)) return;
              try { await api.delete(`/jobs/${jobId}`); toast.success("Job deleted"); window.location.href = "/app/jobs"; }
              catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
            }} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 transition-colors">
              <Trash2 className="w-4 h-4" /> Delete job
            </button>
          )}
          <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          {job.status === "draft" ? (
          <button data-testid="publish-job-btn" onClick={async () => { try { await api.put(`/jobs/${jobId}`, { status: "open" }); toast.success("Job published — you can now invite candidates"); load(); } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); } }} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
            Publish job
          </button>
          ) : can(user, "candidates.invite") && (
          <>
          <button data-testid="bulk-invite-btn" onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 transition-colors">
            <Upload className="w-4 h-4" /> Bulk Invite
          </button>
          <button data-testid="invite-candidate-btn" onClick={() => { setInv((p) => ({ ...p, call_at_local: p.call_at_local || nextSlot(jobTz) })); setOpen(true); }} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">
            <Plus className="w-4 h-4" /> Invite Candidate
          </button>
          </>
          )}
        </>
      }
    >
      <JobHeader job={job} stages={stages} candidateCount={cands.length} />

      <div className="tp-card overflow-hidden" data-testid="candidates-table">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 border-b border-[color:var(--bd-sub)]">
          <div className="flex items-baseline gap-3">
            <div className="font-display font-semibold">Shortlist Matrix</div>
            <div className="text-xs text-slate-500" data-testid="cand-count">{cf.active ? `Showing ${shownCands.length} of ${cands.length} candidates` : `${cands.length} candidates`}</div>
            {cf.active && <button data-testid="cand-clear-filters" onClick={() => setCf(CF_BLANK)} className="text-xs font-medium text-indigo-600 hover:text-indigo-500 inline-flex items-center gap-1"><X className="w-3 h-3" />Clear</button>}
          </div>
          <div className="flex flex-col sm:flex-row gap-2" data-testid="cand-filters">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="cand-search" value={cf.q} onChange={(e) => setCf({ ...cf, q: e.target.value })} placeholder="Search name, email or phone…" className="text-sm px-3 py-1.5 rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none pl-9 w-full sm:w-64" />
            </div>
            <div className="grid grid-cols-2 sm:flex gap-2">
              <select data-testid="cand-filter-stage" value={cf.stage} onChange={(e) => setCf({ ...cf, stage: e.target.value })} className="text-sm px-3 py-1.5 rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
                <option value="">All stages</option>
                {Object.keys(stageColors).map(k => <option key={k} value={k}>{stageLabel(k)}</option>)}
              </select>
              <select data-testid="cand-filter-rec" value={cf.rec} onChange={(e) => setCf({ ...cf, rec: e.target.value })} className="text-sm px-3 py-1.5 rounded-lg bg-[color:var(--bg-card)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
                <option value="">All recommendations</option>
                {REC_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>
        {candsData === undefined ? null : cands.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No candidates yet. Invite one to start the pipeline.</div>
        ) : shownCands.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="no-cand-matches">No candidates match your search. <button onClick={() => setCf(CF_BLANK)} className="text-indigo-600 font-medium">Clear filters</button></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-[color:var(--bd-sub)]">
                <tr>
                  <SortTh k="name" label="Candidate" sort={sort} onSort={toggleSort} />
                  <SortTh k="stage" label="Stage" sort={sort} onSort={toggleSort} />
                  {stages.telephonic && <SortTh k="voice_score" label="Telephonic" right sort={sort} onSort={toggleSort} />}
                  {stages.video && <SortTh k="video_score" label="Video" right sort={sort} onSort={toggleSort} />}
                  {stages.coding && <SortTh k="coding_score" label="Coding" right sort={sort} onSort={toggleSort} />}
                  <SortTh k="overall_score" label="Overall" right sort={sort} onSort={toggleSort} />
                  <th className="text-right px-4 py-3">Recommendation</th>
                  <th className="text-left px-4 py-3">Annotation</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownCands.map((c) => (
                  <tr key={c.id} data-testid={`candidate-row-${c.id}`} className="border-b border-[color:var(--bd-sub)] hover:bg-white/5">
                    <td className="px-4 py-3">
                      <Link to={`/app/candidates/${c.id}`} className="font-medium hover:text-indigo-300">{c.name}</Link>
                      <div className="text-xs text-slate-500">{c.email}</div>
                    </td>
                    <td className="px-4 py-3"><span className={stageColors[c.stage] || "tp-chip"}>{stageLabel(c.stage)}</span>{c.hired && <span className="tp-chip tp-chip-emerald ml-1.5" data-testid={`hired-chip-${c.id}`}>Hired</span>}</td>
                    {stages.telephonic && <td className="px-4 py-3 text-right font-mono">{c.voice_score ?? (c.phone_status === "incomplete" ? <span className="text-amber-600 text-xs" data-testid={`voice-incomplete-${c.id}`}>Incomplete</span> : "—")}</td>}
                    {stages.video && <td className="px-4 py-3 text-right font-mono">{c.video_score ?? (c.video_status === "incomplete" ? <span className="text-amber-600 text-xs" data-testid={`video-incomplete-${c.id}`}>Incomplete</span> : "—")}</td>}
                    {stages.coding && <td className="px-4 py-3 text-right font-mono">{c.coding_score ?? (c.coding_status === "incomplete" ? <span className="text-amber-600 text-xs" data-testid={`coding-incomplete-${c.id}`}>Incomplete</span> : "—")}</td>}
                    <td className="px-4 py-3 text-right font-mono font-semibold">{c.overall_score ?? "—"}</td>
                    <td className="px-4 py-3 text-right text-xs uppercase tracking-wider">
                      {c.recommendation ? <span className="tp-chip tp-chip-indigo">{c.recommendation.replace('_',' ')}</span> : <span className="text-slate-600">pending</span>}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <AnnotationCell cand={c} onSaved={() => reloadCands()} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {can(user, "candidates.invite") && (
                          <button data-testid={`edit-cand-${c.id}`} onClick={() => setEditCand(c)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white" title="Edit name, email or phone"><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                        <button data-testid={`copy-invite-${c.id}`} onClick={() => copyLink(c.invite_token)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white" title="Copy invite link"><Copy className="w-3.5 h-3.5" /></button>
                        <button data-testid={`view-report-${c.id}`} onClick={() => viewReport(c.id)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white" title="View report in a new tab"><ExternalLink className="w-3.5 h-3.5" /></button>
                        <button data-testid={`download-report-${c.id}`} onClick={() => downloadReport(c.id, c.name)} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white" title="Download performance report (PDF)"><FileDown className="w-3.5 h-3.5" /></button>
                        {job.can_delete && (
                        <button data-testid={`delete-cand-${c.id}`} onClick={async () => {
                          if (!window.confirm(`Delete candidate ${c.name}? All their recordings and transcripts will be removed. This can't be undone.`)) return;
                          try { await api.delete(`/candidates/${c.id}`); toast.success("Candidate deleted"); load(); }
                          catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
                        }} className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-400" title="Delete candidate"><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                        <Link to={`/app/candidates/${c.id}`} data-testid={`open-cand-${c.id}`} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white"><ArrowUpRight className="w-3.5 h-3.5" /></Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editCand && <EditCandidateDialog candidate={editCand} countryCode={job.country_code} onClose={() => setEditCand(null)} onSaved={load} />}
      {bulkOpen && <BulkInviteDialog jobId={jobId} phoneStage={phoneStage} existingEmails={new Set(cands.map(c => (c.email || "").toLowerCase()))} onClose={() => setBulkOpen(false)} onDone={load} />}
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={invite} className="tp-card p-6 w-full max-w-md" data-testid="invite-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-semibold">Invite Candidate</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <label className="text-xs text-slate-400 uppercase tracking-wider">Name</label>
            <input data-testid="invite-name" value={inv.candidate_name} onChange={(e)=>setInv({...inv,candidate_name:e.target.value})} required className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
            <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
            <input data-testid="invite-email" value={inv.candidate_email} onChange={(e)=>setInv({...inv,candidate_email:e.target.value})} type="email" required className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
            <label className="text-xs text-slate-400 uppercase tracking-wider">Phone (optional)</label>
            <div className="mt-1 mb-4"><PhoneInput testid="invite-phone" value={inv.candidate_phone} defaultCountry={job.country_code} onChange={(p) => setInv((prev) => ({ ...prev, candidate_phone: p }))} /></div>
            {phoneStage && <InviteCallFields value={inv} tz={jobTz} hasPhone={!!inv.candidate_phone.trim()}
              onChange={(p) => setInv((prev) => ({ ...prev, ...p, ...(p.call_mode === "scheduled" && !prev.call_at_local ? { call_at_local: nextSlot(jobTz) } : {}) }))} />}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-slate-300 hover:text-white">Cancel</button>
              <button data-testid="submit-invite" type="submit" className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white">Create Invite</button>
            </div>
          </form>
        </div>
      )}
      {editOpen && edit && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={requestEditClose}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={saveEdit} onInvalidCapture={() => setInvalidTick((t) => t + 1)} className="tp-card p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-job-form">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Edit</div>
                <h2 className="font-display text-2xl font-semibold">{job.title}</h2>
              </div>
              <button type="button" data-testid="edit-job-close" onClick={requestEditClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="grid gap-3">
              <FormSection id="edit-basics" done={sectionDone(edit).basics} missing={missingField(edit, "edit-job").basics} title="Basics" defaultOpen forceOpen={invalidTick} summary={edit.title}>
              <JdUpload prefix="edit-job" confirmReplace hasDescription={!!edit.description?.trim()} onParsed={(d) => setEdit((prev) => applyParsedJd(prev, d))} />
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Job Title</label>
                <input data-testid="edit-job-title" value={edit.title} onChange={(e)=>setEdit({...edit,title:e.target.value})} required className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              </div>
              <JobClientFields value={edit} onChange={setEdit} prefix="edit-job" />
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Status</label>
                  <select data-testid="edit-job-status" value={edit.status} onChange={(e)=>setEdit({...edit,status:e.target.value})} className="tp-select w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
                    {JOB_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-wider">Job Description</label>
                <textarea data-testid="edit-job-description" value={edit.description} onChange={(e)=>setEdit({...edit,description:e.target.value})} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              </div>
              </FormSection>
              <FormSection id="edit-skills" done={sectionDone(edit).skills} missing={missingField(edit, "edit-job").skills} title="Skills" forceOpen={invalidTick} summary={skillSummary(edit)}>
              <JobSkillsFields title={edit.title} description={edit.description} must={edit.must_haves} nice={edit.nice_to_haves}
                onChange={(p) => setEdit((prev) => ({ ...prev, ...p }))} prefix="edit-job" />
              </FormSection>
              <FormSection id="edit-location" done={sectionDone(edit).location} missing={missingField(edit, "edit-job").location} title="Location & Pay" forceOpen={invalidTick} summary={locationSummary(edit)}>
              <div className="grid grid-cols-1 sm:grid-cols-[1.3fr_1fr_auto] gap-3 items-end" data-testid="edit-job-location-fields">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Country</label>
                  <select
                    data-testid="edit-job-country"
                    value={edit.country_code}
                    onChange={(e) => {
                      const code = e.target.value;
                      const meta = COUNTRY_BY_CODE[code];
                      setEdit((prev) => ({ ...prev, country_code: code, salary_currency: meta?.currency || prev.salary_currency }));
                    }}
                    className="tp-select w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">City (optional)</label>
                  <input data-testid="edit-job-city" value={edit.city} onChange={(e)=>setEdit({...edit,city:e.target.value})} placeholder="Bengaluru / Berlin / Remote" className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-300 pb-2.5 whitespace-nowrap">
                  <input data-testid="edit-job-remote" type="checkbox" checked={!!edit.remote} onChange={(e)=>setEdit({...edit,remote:e.target.checked})} className="w-4 h-4" />
                  Remote
                </label>
              </div>
              <div className="grid grid-cols-[1fr_1fr_1fr_0.9fr] gap-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Currency</label>
                  <select data-testid="edit-job-currency" value={edit.salary_currency} onChange={(e)=>setEdit({...edit,salary_currency:e.target.value})} className="tp-select w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none">
                    {Array.from(new Set(COUNTRIES.map(c => c.currency))).sort().map((cur) => (
                      <option key={cur} value={cur}>{cur}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Salary Min</label>
                  <input data-testid="edit-job-salary-min" value={edit.salary_min} onChange={(e)=>setEdit({...edit,salary_min:e.target.value})} type="number" step="any" className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Salary Max</label>
                  <input data-testid="edit-job-salary-max" value={edit.salary_max} onChange={(e)=>setEdit({...edit,salary_max:e.target.value})} type="number" step="any" className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Max Notice (d)</label>
                  <input value={edit.max_notice_days} onChange={(e)=>setEdit({...edit,max_notice_days:e.target.value})} type="number" className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
                </div>
              </div>
              <SalaryUnitToggle form={edit} setForm={setEdit} testid="edit-job-salary-unit" />
              {(edit.salary_min || edit.salary_max) ? (
                <div className="-mt-1 text-[11px] text-indigo-300" data-testid="edit-salary-preview">
                  AI will say: <span className="font-mono text-slate-300">{fmtSalaryBand(salaryFromInput(edit.salary_min, edit.salary_unit), salaryFromInput(edit.salary_max, edit.salary_unit), edit.salary_currency, edit.salary_unit)}</span>
                </div>
              ) : null}
              </FormSection>
              <FormSection id="edit-stages" done={sectionDone(edit).stages} missing={missingField(edit, "edit-job").stages} title="Stages & Questions" forceOpen={invalidTick} summary={stageSummary(edit)}>
              <div className="pt-2">
                <label className="text-xs text-slate-400 uppercase tracking-wider">Interview Stages</label>
                <div className="text-[11px] text-slate-500 mt-0.5 mb-2">Pick which rounds this role will run. At least one is required.</div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {[
                    { k: "telephonic", label: "Telephonic Interview", Icon: Phone, hint: "AI-led conversational screen" },
                    { k: "video", label: "Video Interview", Icon: Video, hint: "Structured on-camera round" },
                    { k: "coding", label: "Coding Interview", Icon: Code2, hint: "Live coding + proctoring" },
                  ].map(({ k, label, Icon, hint }) => {
                    const on = !!edit.stages[k];
                    return (
                      <button
                        type="button"
                        key={k}
                        data-testid={`edit-job-stage-${k}`}
                        aria-pressed={on}
                        onClick={() => setEdit({ ...edit, stages: { ...edit.stages, [k]: !on } })}
                        className={`text-left p-3 rounded-lg border transition-colors ${on ? "border-indigo-500/60 bg-indigo-500/10" : "border-[color:var(--bd-sub)] hover:border-[color:var(--bd-hi)] bg-[color:var(--bg-elev)]"}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-4 h-4 rounded border grid place-items-center ${on ? "bg-indigo-600 border-indigo-500" : "border-slate-500"}`}>
                            {on ? <span className="w-2 h-2 bg-white rounded-sm"/> : null}
                          </span>
                          <Icon className={`w-4 h-4 ${on ? "text-indigo-300" : "text-slate-400"}`} />
                          <span className={`text-sm font-medium ${on ? "text-indigo-100" : "text-slate-200"}`}>{label}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 pl-6">{hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              {edit.stages?.telephonic && (
                <PhoneLanguageSelect testid="edit-job-phone-language" value={edit.phone_language} onChange={(v) => setEdit((prev) => ({ ...prev, phone_language: v }))} />
              )}
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Knockout Questions (one per line)</label>
                  <button type="button" data-testid="edit-gen-knockout-btn" onClick={() => generateQuestions("knockout")} disabled={genBusy !== null} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-indigo-500/40 text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 transition-colors disabled:opacity-50">
                    {genBusy === "knockout" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>} Generate with AI
                  </button>
                </div>
                <textarea value={edit.knockout_questions} onChange={(e)=>setEdit({...edit,knockout_questions:e.target.value})} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Video Questions (one per line)</label>
                  <button type="button" data-testid="edit-gen-video-btn" onClick={() => generateQuestions("video")} disabled={genBusy !== null} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md border border-indigo-500/40 text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 transition-colors disabled:opacity-50">
                    {genBusy === "video" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Sparkles className="w-3 h-3"/>} Generate with AI
                  </button>
                </div>
                <textarea value={edit.video_questions} onChange={(e)=>setEdit({...edit,video_questions:e.target.value})} rows={3} className="w-full mt-1 px-3 py-2 rounded-lg bg-[color:var(--bg-elev)] border border-[color:var(--bd-sub)] focus:border-indigo-500 outline-none" />
              </div>
              {edit.stages.telephonic && <PhonePlanEditor value={edit.phone_plan} onChange={(v) => setEdit((prev) => ({ ...prev, phone_plan: v }))} ctx={planCtx(edit, salaryFromInput)} />}
              </FormSection>
              {edit.stages.coding && (
                <FormSection id="edit-coding" done={sectionDone(edit).coding} missing={missingField(edit, "edit-job").coding} title="Coding Round" forceOpen={invalidTick} summary={codingSummary(edit)}>
              {edit.stages.coding && <CodingRoundSection value={edit} onChange={setEdit} ctx={{ title: edit.title, description: edit.description, must_haves: edit.must_haves }} prefix="edit-job" />}
                </FormSection>
              )}
            </div>
            <div data-testid="job-form-actions" className="sticky -bottom-6 -mx-6 -mb-6 mt-6 px-6 py-3 flex justify-end gap-2 bg-[color:var(--bg-card,#fff)] border-t border-[color:var(--bd-sub)] z-20">
              <button type="button" data-testid="edit-job-cancel" onClick={requestEditClose} className="px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors">Cancel</button>
              <button data-testid="submit-edit-job" type="submit" className="px-4 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors">Save Changes</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
